import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_LABEL,
  getStatusLabel,
  type SupportCategory,
  type SupportTicketDto,
  type SupportTicketStatus,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Roles that can claim / transition / reply. Mirrors the server-side
// STAFF_SUPPORT_WRITE_ROLES set in routes/support.ts. Defence-in-depth + UX
// affordance — the server is the authority, but readonly / compliance /
// finance staff don't see Claim buttons so they don't bother trying.
const SUPPORT_WRITE_ROLES = new Set(['super', 'admin', 'lead', 'analyst']);

// Role labels — kept aligned with AdminLayout's ROLE_LABELS so the same
// staff member reads the same way across the sidebar and the support pages.
const ROLE_LABELS: Record<string, string> = {
  super: 'Super Admin', admin: 'Admin', lead: 'Engagement Lead',
  analyst: 'Analyst', compliance: 'Compliance', finance: 'Finance', readonly: 'Read-only',
};

// MVP never writes `closed`. Show it as a tab only if a row needs it for
// the legacy seed data — but for a uniform UX we keep the tab set lean.
const STATUS_TABS: SupportTicketStatus[] = [
  'open', 'acknowledged', 'in_progress', 'awaiting_client', 'resolved',
];

const STATUS_TONE: Record<SupportTicketStatus, string> = {
  open:            'amber',
  acknowledged:    'accent',
  in_progress:     'accent',
  awaiting_client: 'amber',
  resolved:        'green',
  closed:          '',
};

type OwnerFilter = 'all' | 'me' | 'unassigned';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86_400) return `${Math.round(diffSec / 86_400)}d ago`;
  return iso.slice(0, 10);
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

export function AdminSupport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<SupportTicketDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);

  // URL-driven filter state — deep-linkable, restored on reload.
  const statusFilter = (searchParams.get('status') ?? 'all') as 'all' | SupportTicketStatus;
  const ownerFilter = (searchParams.get('owner') ?? 'all') as OwnerFilter;
  const categoryFilter = (searchParams.get('category') ?? 'all') as 'all' | SupportCategory;

  const role = user?.role ?? '';
  const canWrite = SUPPORT_WRITE_ROLES.has(role);

  const queryString = useMemo(() => {
    const qp = new URLSearchParams();
    if (statusFilter !== 'all') qp.set('status', statusFilter);
    if (ownerFilter !== 'all') qp.set('owner', ownerFilter);
    if (categoryFilter !== 'all') qp.set('category', categoryFilter);
    const s = qp.toString();
    return s ? `?${s}` : '';
  }, [statusFilter, ownerFilter, categoryFilter]);

  const loadList = useCallback(() => {
    setRows(null);
    setError(null);
    apiGet<{ tickets: SupportTicketDto[] }>(`/api/support${queryString}`)
      .then((r) => setRows(r.tickets))
      .catch((err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load tickets.'));
  }, [queryString]);

  useEffect(() => { loadList(); }, [loadList]);

  // Mutate-in-place helper for the URL search params — drop "all" so the
  // URL stays compact and shareable.
  function setFilter(key: 'status' | 'owner' | 'category', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  async function handleClaim(e: React.MouseEvent, ticketId: string) {
    e.stopPropagation();
    if (claiming || !canWrite) return;
    setClaiming(ticketId);
    try {
      const r = await apiPost<{ ticket: SupportTicketDto }>(`/api/support/${ticketId}/claim`, {});
      // Optimistic local update — replace the row in place.
      setRows((prev) => (prev ?? []).map((t) => (t.id === r.ticket.id ? r.ticket : t)));
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not claim ticket.');
    } finally {
      setClaiming(null);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">Support</h1>
          <p className="portal-sub">Triage and respond to client tickets.</p>
        </div>
      </div>

      {/* Status tabs */}
      <div className="tabs" style={{ marginTop: 24 }}>
        <button
          className={statusFilter === 'all' ? 'on' : ''}
          onClick={() => setFilter('status', 'all')}
        >
          All
        </button>
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            className={statusFilter === s ? 'on' : ''}
            onClick={() => setFilter('status', s)}
          >
            {getStatusLabel(s, 'staff')}
          </button>
        ))}
      </div>

      {/* Owner segmented + Category dropdown */}
      <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="tabs" style={{ margin: 0 }}>
          {(['all', 'me', 'unassigned'] as OwnerFilter[]).map((o) => (
            <button
              key={o}
              className={ownerFilter === o ? 'on' : ''}
              onClick={() => setFilter('owner', o)}
            >
              {o === 'all' ? 'All' : o === 'me' ? 'Mine' : 'Unassigned'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Category
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setFilter('category', e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="all">All categories</option>
            {SUPPORT_CATEGORIES.map((c) => (
              <option key={c} value={c}>{SUPPORT_CATEGORY_LABEL[c]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card flush" style={{ marginTop: 18 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Subject</th>
              <th>Client</th>
              <th>Category</th>
              <th>Status</th>
              <th>Owner</th>
              <th>Opened</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows === null && !error && (
              <tr><td colSpan={8} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {error && (
              <tr><td colSpan={8} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>
            )}
            {rows !== null && !error && rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No tickets matching these filters.
                </td>
              </tr>
            )}
            {(rows ?? []).map((t) => {
              const ownerLabel = t.owner_user_id
                ? `${t.owner_name ?? '—'}${t.owner_role ? ` · ${ROLE_LABELS[t.owner_role] ?? t.owner_role}` : ''}`
                : 'Unassigned';
              const showClaim = canWrite && t.owner_user_id === null;
              return (
                <tr
                  key={t.id}
                  className="row-clickable"
                  onClick={() => navigate(`/admin/support/${t.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/admin/support/${t.id}`);
                    }
                  }}
                >
                  <td><span className="rid">{t.id}</span></td>
                  <td style={{ fontSize: 13.5 }}>{truncate(t.subject, 60)}</td>
                  <td><span className="rid">{t.client_id}</span></td>
                  <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{SUPPORT_CATEGORY_LABEL[t.category]}</td>
                  <td>
                    <span className={`chip ${STATUS_TONE[t.status]}`}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                      {getStatusLabel(t.status, 'staff')}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: t.owner_user_id ? 'var(--fg)' : 'var(--fg-mute)' }}>
                    {ownerLabel}
                  </td>
                  <td style={{ color: 'var(--fg-mute)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                    {relativeTime(t.created_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {showClaim && (
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={(e) => handleClaim(e, t.id)}
                        disabled={claiming === t.id}
                        style={{ fontSize: 12, padding: '4px 10px' }}
                      >
                        {claiming === t.id ? 'Claiming…' : 'Claim'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
